import { useState } from 'react';
import type {
  AccDatei, AccHinweis, AccHub, AccInhalt, AccOrdner, AccProjektEintrag, AccVersion, ApiClient,
} from '../../api';
import { ContentSection, EmptyState, ErrorState, Feld, StatusBadge } from '../../muster';
import { zeitpunkt } from './acczeit';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * „ACC DURCHSUCHEN“ (AC1b) - nur lesend
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Hub → Projekt → Ordner → Dateien mit der jüngsten Version, so wie das verbundene Konto sie sieht. Geladen wird
 * erst auf Klick und jeder Ordner erst beim Aufklappen: ein ACC-Projekt kann tausende Ordner haben. Ein Revit-Cloud-
 * Modell trägt die beiden Kennungen, mit denen die Agenten es öffnen („Cloud-Modell“).
 */

type Zweig = { zustand: 'laedt' } | { zustand: 'fehler'; text: string } | { zustand: 'da'; inhalt: AccInhalt };

/** Die Meldung des Servers; 403 heißt hier: keine Rolle in TGAPlan. */
export function accFehlertext(e: unknown): string {
  const t = e instanceof Error ? e.message : String(e);
  return t === 'HTTP 403' ? 'ACC durchsuchen dürfen nur ProjectAdmin und SystemAdmin.' : t;
}

/** „V7 · 20.10.2026, 11:30“ */
export function versionText(v: AccVersion | null): string {
  if (!v) return 'ohne Version';
  const am = zeitpunkt(v.am);
  return am ? `V${v.nummer} · ${am}` : `V${v.nummer}`;
}

export function AccDurchsuchen({ api, beiProjektwahl }: {
  api: ApiClient;
  /** Meldet die Wahl von Hub und Projekt (AC1c: Zuordnung). */
  beiProjektwahl?: (hub: AccHub, projekt: AccProjektEintrag) => void;
}) {
  const [hubs, setHubs] = useState<AccHub[] | null>(null);
  const [warnungen, setWarnungen] = useState<AccHinweis[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [hubId, setHubId] = useState('');
  const [projekte, setProjekte] = useState<AccProjektEintrag[] | null>(null);
  const [projektId, setProjektId] = useState('');
  const [oben, setOben] = useState<AccOrdner[] | null>(null);
  const [zweige, setZweige] = useState<Record<string, Zweig>>({});
  const [versionen, setVersionen] = useState<Record<string, AccVersion[] | 'laedt' | string>>({});

  const zuruecksetzen = (ab: 'hub' | 'projekt') => {
    if (ab === 'hub') { setProjekte(null); setProjektId(''); }
    setOben(null);
    setZweige({});
    setVersionen({});
  };

  const projekteLaden = async (hub: string) => {
    setHubId(hub);
    zuruecksetzen('hub');
    if (!hub) return;
    try {
      setProjekte(await api.accProjekte(hub));
    } catch (e) { setFehler(accFehlertext(e)); }
  };

  const laden = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      const r = await api.accHubs();
      setHubs(r.hubs);
      setWarnungen(r.warnungen);
      if (r.hubs.length === 1) await projekteLaden(r.hubs[0].id);
    } catch (e) {
      setFehler(accFehlertext(e));
    } finally {
      setLaeuft(false);
    }
  };

  const projektWaehlen = async (id: string) => {
    setProjektId(id);
    zuruecksetzen('projekt');
    if (!id) return;
    const hub = hubs?.find((h) => h.id === hubId);
    const projekt = projekte?.find((p) => p.id === id);
    if (hub && projekt) beiProjektwahl?.(hub, projekt);
    try {
      setOben(await api.accOberordner(hubId, id));
    } catch (e) { setFehler(accFehlertext(e)); }
  };

  const umschalten = async (ordner: AccOrdner) => {
    if (zweige[ordner.id]) {
      setZweige((z) => { const n = { ...z }; delete n[ordner.id]; return n; });
      return;
    }
    setZweige((z) => ({ ...z, [ordner.id]: { zustand: 'laedt' } }));
    try {
      const inhalt = await api.accInhalt(projektId, ordner.id);
      setZweige((z) => ({ ...z, [ordner.id]: { zustand: 'da', inhalt } }));
    } catch (e) {
      setZweige((z) => ({ ...z, [ordner.id]: { zustand: 'fehler', text: accFehlertext(e) } }));
    }
  };

  const versionenUmschalten = async (datei: AccDatei) => {
    if (versionen[datei.id]) {
      setVersionen((v) => { const n = { ...v }; delete n[datei.id]; return n; });
      return;
    }
    setVersionen((v) => ({ ...v, [datei.id]: 'laedt' }));
    try {
      const liste = await api.accVersionen(projektId, datei.id);
      setVersionen((v) => ({ ...v, [datei.id]: liste }));
    } catch (e) {
      setVersionen((v) => ({ ...v, [datei.id]: accFehlertext(e) }));
    }
  };

  const ordnerListe = (liste: AccOrdner[], tiefe: number) => liste.map((o) => {
    const z = zweige[o.id];
    return (
      <li key={o.id} data-ordner={o.name}>
        <button className="m-knopf acc-ordner" aria-expanded={!!z} onClick={() => umschalten(o)}
                style={{ marginLeft: `${tiefe}em` }}>
          {z ? '▾' : '▸'} {o.name}
        </button>
        {z?.zustand === 'laedt' && <span className="sehr-schwach"> lädt …</span>}
        {z?.zustand === 'fehler' && <ErrorState klein meldung={z.text} />}
        {z?.zustand === 'da' && (
          <ul className="acc-baum">
            {ordnerListe(z.inhalt.ordner, tiefe + 1)}
            {z.inhalt.dateien.map((d) => dateiZeile(d, tiefe + 1))}
            {z.inhalt.ordner.length === 0 && z.inhalt.dateien.length === 0 && (
              <li className="sehr-schwach" style={{ marginLeft: `${tiefe + 1}em` }}>leer</li>
            )}
          </ul>
        )}
      </li>
    );
  });

  const dateiZeile = (d: AccDatei, tiefe: number) => {
    const v = versionen[d.id];
    return (
      <li key={d.id} data-datei={d.name} style={{ marginLeft: `${tiefe}em` }}>
        <span className="acc-datei">{d.name}</span>{' '}
        <span className="sehr-schwach">{versionText(d.tip)}</span>{' '}
        {d.tip?.istCloudModell && <StatusBadge zustand="info" klein text="Cloud-Modell" titel={d.tip.modelGuid ?? undefined} />}{' '}
        <button className="m-knopf" onClick={() => versionenUmschalten(d)}>{v ? 'Versionen zu' : 'Versionen'}</button>
        {v === 'laedt' && <span className="sehr-schwach"> lädt …</span>}
        {typeof v === 'string' && v !== 'laedt' && <ErrorState klein meldung={v} />}
        {Array.isArray(v) && (
          <ol className="acc-versionen" data-versionen>
            {v.map((x) => <li key={x.id}>{versionText(x)}</li>)}
          </ol>
        )}
      </li>
    );
  };

  return (
    <ContentSection
      titel="ACC durchsuchen"
      beschreibung="Nur lesend: Hubs, Projekte, Ordner und Dateien, wie das verbundene Autodesk-Konto sie sieht."
      aktionen={<button className="m-knopf" onClick={laden} disabled={laeuft}>{hubs ? 'Neu laden' : 'Laden'}</button>}
    >
      {fehler && <ErrorState klein meldung={fehler} />}
      {warnungen.map((w) => (
        <p key={w.code + w.text} data-warnung><StatusBadge zustand="warnung" text="Hinweis von Autodesk" /> {w.text}</p>
      ))}
      {hubs && hubs.length === 0 && (
        <EmptyState klein titel="Kein Hub sichtbar"
                    erklaerung="Das verbundene Konto sieht keinen ACC-Hub - oder die App ist dort nicht freigegeben." />
      )}
      {hubs && hubs.length > 0 && (
        <Feld art="auswahl" beschriftung="Hub" wert={hubId} platzhalter="Hub wählen …"
              optionen={hubs.map((h) => ({ wert: h.id, beschriftung: h.region ? `${h.name} (${h.region})` : h.name }))}
              beiAenderung={(w) => { projekteLaden(w as string); }} />
      )}
      {projekte && (
        <Feld art="auswahl" beschriftung="ACC-Projekt" wert={projektId} platzhalter="Projekt wählen …"
              optionen={projekte.map((p) => ({ wert: p.id, beschriftung: p.name }))}
              beiAenderung={(w) => { projektWaehlen(w as string); }} />
      )}
      {oben && (
        oben.length === 0
          ? <EmptyState klein titel="Keine Ordner sichtbar" />
          : <ul className="acc-baum" data-baum>{ordnerListe(oben, 0)}</ul>
      )}
    </ContentSection>
  );
}
